<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Verify gate fired when a registered agent clicks the developer's
 * account-manager contact (phone / email / WhatsApp) on a project.
 */

const { t } = useI18n();

const {
  developerName,
  managerName,
  managerRole,
  userPhoneMasked = '+971 50 ••• ••24',
} = defineProps<{
  developerName?: string;
  managerName?: string;
  managerRole?: string;
  userPhoneMasked?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'verify-start'): void;
  (e: 'change-number'): void;
}>();

const titleId = useId();

const developerNameText = computed(() => developerName ?? t('property.shell.theDeveloper'));
const managerNameText = computed(() => managerName ?? t('property.access.shared.accountManager'));
const managerRoleText = computed(() => managerRole ?? t('property.access.shared.accountManager'));

const managerInitials = computed(() =>
  managerNameText.value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join(''),
);
</script>

<template>
  <ModalShell
    accent="green"
    :labelled-by="titleId"
    @close="emit('close')"
  >
    <ModalIcon
      kind="phone"
      accent="green"
    />

    <div class="mb-5 flex items-center gap-3 rounded-xl border border-success/25 bg-success/05 p-3">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-success/80 to-success text-[14px] font-bold text-white"
      >
        {{ managerInitials || 'AM' }}
      </div>
      <div class="min-w-0 flex-1">
        <div class="font-mono text-[9.5px] font-bold uppercase tracking-[0.1em] text-success">
          {{
            t('property.access.modalVerifyDevContact.developerAccountManagerLabel', { developer: developerNameText })
          }}
        </div>
        <div class="truncate text-[13.5px] font-semibold text-black-100">{{ managerNameText }}</div>
        <div class="truncate text-[11.5px] text-black-60">{{ managerRoleText }}</div>
      </div>
    </div>

    <h2
      :id="titleId"
      class="mb-3 font-serif text-[24px] font-semibold leading-[1.15] tracking-[-0.015em] text-black-100"
    >
      {{ t('property.access.modalVerifyDevContact.heading', { developer: developerNameText }) }}
    </h2>
    <p class="mb-5 text-[13.5px] leading-[1.55] text-black-70">
      {{ t('property.access.modalVerifyDevContact.body') }}
    </p>

    <ul class="mb-6 space-y-2">
      <CheckBullet accent="green">{{ t('property.access.modalVerifyDevContact.bullet1') }}</CheckBullet>
      <CheckBullet accent="green">{{ t('property.access.modalVerifyDevContact.bullet2') }}</CheckBullet>
      <CheckBullet accent="green">{{ t('property.access.modalVerifyDevContact.bullet3') }}</CheckBullet>
    </ul>

    <div class="flex flex-col gap-2">
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center rounded-[10px] bg-success px-5 text-[14px] font-bold text-white transition-colors hover:bg-success/90"
        @click="emit('verify-start')"
      >
        <span
          >{{ t('property.access.shared.verifyThirtySec') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></span
        >
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center rounded-[10px] text-[13px] font-semibold text-black-80 transition-colors hover:bg-black-05"
        @click="emit('close')"
      >
        {{ t('property.access.shared.maybeLater') }}
      </button>
    </div>
    <div class="mt-4 text-center text-[11.5px] text-black-60">
      <span dir="ltr">{{ userPhoneMasked }}</span> ·
      <button
        type="button"
        class="font-semibold text-black-80 underline-offset-2 hover:underline"
        @click="emit('change-number')"
      >
        {{ t('property.access.shared.changeNumber') }}
      </button>
    </div>
  </ModalShell>
</template>
